/**
 * EL RESUMEN DE VENTAS — un solo lugar para los tres rangos.
 *
 * DE DÓNDE SALEN LOS NÚMEROS
 * ──────────────────────────
 * De `metrics/ecommerce/daily/{fecha}`, y de nada más. La alternativa que se
 * evaluó era leer "hoy" de `ventasHistorial` en los chats y semana/mes del
 * agregado: dos lectores del mismo número, que es el patrón de divergencia que
 * este repo ya tiene anotado tres veces. El agregador pasó a correr cada hora
 * justamente para que una sola fuente pueda responder los tres rangos.
 *
 * POR QUÉ LA CONVERSIÓN SE CALCULA ACÁ Y NO SE LEE DEL DOCUMENTO
 * ─────────────────────────────────────────────────────────────
 * El documento trae `conversionRate`, pero es **por pedidos**
 * (`botAssistedOrders / totalOrders`), no por monto. Medido sobre el 11-ago-2026:
 * 75% por pedidos contra **73,3%** por monto — el mismo día, dos números
 * distintos. La tarjeta habla en pesos, así que tiene que dividir pesos.
 *
 * Y hay algo peor que usar el número equivocado: `conversionRate` es un
 * porcentaje POR DÍA. Promediar los porcentajes de varios días —que es lo que
 * hacía el componente que esta tarjeta reemplaza— le da el mismo peso a un día de
 * una venta que a uno de cincuenta. La única forma correcta de agregarlo es
 * sumar los montos y dividir al final.
 *
 * DÍAS MEDIDOS Y DÍAS DEL RANGO
 * ─────────────────────────────
 * El agregador escribe el documento del día AUNQUE NO HAYA VENTAS. Entonces:
 *
 *   documento presente  → ese día se midió; `totalOrders: 0` significa "no vendiste"
 *   documento ausente   → ese día NO se midió
 *
 * La distinción ya está en los datos y no hace falta inventar un campo. Se suman
 * los días medidos y se dice cuántos son: un "mes" de $254.660 sobre 3 días
 * medidos es honesto si lo declara, y engañoso si se presenta como el mes.
 */

/** Los rangos que muestra la tarjeta, en el orden en que se muestran. */
export const RANGOS = ['hoy', 'semana', 'mes'];

/** Cuántos días cubre "semana". */
export const DIAS_DE_LA_SEMANA = 7;

/**
 * El día calendario (`YYYY-MM-DD`) de un instante en una zona.
 *
 * Es la MISMA línea que `diaLocal` en el agregador, a propósito: los documentos
 * están indexados por el día local del tenant, así que el frontend tiene que
 * preguntar por el mismo día o va a buscar el documento equivocado. Un usuario
 * mirando desde otro huso vería "hoy" de su reloj y no del negocio.
 *
 * Lo que NO se duplica es el cálculo de los BORDES del día —la parte difícil, con
 * los días de 23 y 25 horas—: eso vive sólo en el backend, porque acá no hace
 * falta. Se copia la línea trivial y se deja afuera la complicada.
 */
export function diaEnZona(instante, zona) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(instante);
}

/**
 * La zona con la que el AGREGADOR escribió estos documentos.
 *
 * Se lee de los propios documentos (`zonaHoraria`) en vez de usar la del
 * navegador: si el dueño abre el tablero desde otro país, el día que quiere ver
 * es el de su negocio, no el de su reloj.
 */
export function zonaDeLosDocs(docs, respaldo) {
  const conZona = (docs || []).filter((d) => d?.zonaHoraria);
  if (conZona.length === 0) {
    return respaldo || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }
  // El más reciente manda: si la configuración cambió, la que vale es la última.
  return conZona.slice().sort((a, b) => String(a.date).localeCompare(String(b.date))).pop().zonaHoraria;
}

/** `YYYY-MM-DD` menos `n` días, como etiqueta de calendario. */
function restarDias(fecha, n) {
  return new Date(Date.parse(`${fecha}T00:00:00Z`) - n * 86400000).toISOString().slice(0, 10);
}

/**
 * Las fechas que cubre un rango, terminando en `hoy`.
 *
 * La aritmética se hace en UTC a propósito y no es un descuido: acá `hoy` ya NO
 * es un instante sino una ETIQUETA de calendario que el backend eligió usando la
 * zona del tenant. Sumar y restar días sobre etiquetas en UTC es exacto —UTC no
 * tiene horario de verano—, y volver a meter una zona acá reintroduciría el
 * problema que el backend ya resolvió.
 *
 * "mes" es el mes CALENDARIO hasta hoy, no los últimos 30 días: es contra lo que
 * el dueño compara su propia contabilidad.
 */
export function fechasDelRango(rango, hoy) {
  if (rango === 'hoy') return [hoy];

  if (rango === 'semana') {
    return Array.from({ length: DIAS_DE_LA_SEMANA }, (_, i) => restarDias(hoy, i)).reverse();
  }

  const primeroDelMes = `${hoy.slice(0, 7)}-01`;
  const fechas = [];
  for (let f = primeroDelMes; f <= hoy; f = restarDias(f, -1)) fechas.push(f);
  return fechas;
}

/**
 * Ventas, monto y cobertura de un rango.
 *
 * `diasMedidos` NO es `fechas.length`: es cuántos de esos días tienen documento.
 * Es el dato que hace honesta la suma.
 */
export function resumirRango(docs, rango, hoy) {
  const fechas = new Set(fechasDelRango(rango, hoy));
  const delRango = (docs || []).filter((d) => fechas.has(d?.date));

  const ventas = delRango.reduce((s, d) => s + (Number(d.totalOrders) || 0), 0);
  const monto = delRango.reduce((s, d) => s + (Number(d.totalRevenue) || 0), 0);

  const conMoneda = delRango.filter((d) => d.currency);
  const moneda = conMoneda.length
    ? conMoneda.slice().sort((a, b) => a.date.localeCompare(b.date)).pop().currency
    : null;

  return { ventas, monto, moneda, diasMedidos: delRango.length, diasDelRango: fechas.size };
}

/**
 * Cuántos pesos de cada cien salieron de una conversación, en el mes.
 *
 * POR MONTO, no por pedidos, y sumando antes de dividir. Devuelve `null` cuando
 * no hay monto: sin ventas no hay proporción que informar, y un "0%" ahí se lee
 * como "el canal no sirve" en vez de "todavía no hay datos".
 */
export function conversionPorMonto(docs, hoy, rango = 'mes') {
  const fechas = new Set(fechasDelRango(rango, hoy));
  const delRango = (docs || []).filter((d) => fechas.has(d?.date));

  const total = delRango.reduce((s, d) => s + (Number(d.totalRevenue) || 0), 0);
  if (!(total > 0)) return null;

  const asistido = delRango.reduce((s, d) => s + (Number(d.botAssistedRevenue) || 0), 0);
  return { deCadaCien: Math.round((asistido / total) * 100), monto: asistido, total };
}

/**
 * El estado de la tarjeta.
 *
 *   `sin-mediciones` — el agregador todavía no escribió ningún documento. Es el
 *     único momento donde el sello de actualización no alcanza, porque no hay
 *     nada que sellar, y es exactamente cuando "todavía no calculé" se confunde
 *     con "no vendiste". Se apaga solo con la primera corrida y no vuelve.
 *   `medido` — hay documentos; si el número es cero, el cero es un hecho.
 */
export function estadoDeVentas(docs) {
  return (docs || []).length === 0 ? 'sin-mediciones' : 'medido';
}

/** El `computedAt` más reciente, para el sello. */
export function ultimaActualizacion(docs) {
  const fechas = (docs || [])
    .map((d) => (d?.computedAt?.toDate ? d.computedAt.toDate() : d?.computedAt))
    .map((x) => (x instanceof Date ? x : new Date(x)))
    .filter((x) => x instanceof Date && Number.isFinite(x.getTime()));
  if (fechas.length === 0) return null;
  return new Date(Math.max(...fechas.map((f) => f.getTime())));
}

/**
 * El nombre del canal, sólo si hay UNO solo activo.
 *
 * Con dos canales encendidos no se puede decir de cuál salió la venta sin
 * mirarlo pedido por pedido, así que la frase queda genérica. Nombrar el
 * equivocado es peor que no nombrar ninguno.
 */
export function nombreDeCanalUnico(integrations) {
  const NOMBRES = { whatsapp: 'WhatsApp', instagram: 'Instagram', messenger: 'Messenger', tiktok: 'TikTok' };
  const activos = Object.entries(integrations || {})
    .filter(([, v]) => v === true)
    .map(([k]) => NOMBRES[k])
    .filter(Boolean);
  return activos.length === 1 ? activos[0] : null;
}
